import { TranslateService } from '@ngx-translate/core';
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { BehaviorSubject, combineLatest, startWith } from 'rxjs';

export interface ITabTitle {
  titleAr: string;
  titleEn: string;
}

export interface IMetaDescription {
  descAr: string;
  descEn: string;
}

@Injectable({
  providedIn: 'root',
})
export class TabTitleService {
  public pageTitle$: BehaviorSubject<ITabTitle> =
    new BehaviorSubject<ITabTitle>({} as ITabTitle);

  public pageDescription$: BehaviorSubject<IMetaDescription> =
    new BehaviorSubject<IMetaDescription>({} as IMetaDescription);

  constructor(
    private title: Title,
    private meta: Meta,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private dom: Document
  ) {
    combineLatest([
      this.translateService.stream('homeTitle'),
      this.pageTitle$,
      this.pageDescription$,
      this.translateService.onLangChange.pipe(startWith(null)),
    ]).subscribe(([baseTitle, pageTitle, pageDesc]) => {
      this.applyTitleAndMeta(baseTitle, pageTitle, pageDesc);
    });
  }

  private applyTitleAndMeta(baseTitle: string, pageTitle: ITabTitle, pageDesc: IMetaDescription): void {
    const lang = this.translateService.currentLang || 'ar';
    
    // Update Title
    if (pageTitle && pageTitle.titleAr && pageTitle.titleEn) {
      const specificTitle = lang === 'ar' ? pageTitle.titleAr : pageTitle.titleEn;
      this.title.setTitle(` ${specificTitle} | ${baseTitle}`);
    } else {
      this.title.setTitle(baseTitle);
    }

    // Update Meta Description
    const defaultDescAr = 'معين للمكتبات الرقمية، صرح معرفي بالمدينة المنورة يُعنى بحفظ التراث الإسلامي والمخطوطات النادرة وتقديم خدمات بحثية متطورة.';
    const defaultDescEn = 'Maeen Digital Libraries in Medina preserves Islamic heritage and rare manuscripts, offering advanced research services.';
    
    let desc = '';
    if (lang === 'ar') {
      desc = pageDesc?.descAr || defaultDescAr;
    } else {
      desc = pageDesc?.descEn || defaultDescEn;
    }
      
    this.meta.updateTag({ name: 'description', content: desc });

    // Update Canonical URL
    const canonicalURL = this.dom.URL;
    let link: HTMLLinkElement | null = this.dom.querySelector("link[rel='canonical']");
    if (!link) {
      link = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.dom.head.appendChild(link);
    }
    link.setAttribute('href', canonicalURL);
  }

  // Deprecated but kept for compatibility if called directly
  public updateTitle(): void {
    this.translateService.get('homeTitle').subscribe(base => {
        this.applyTitleAndMeta(base, this.pageTitle$.getValue(), this.pageDescription$.getValue());
    });
  }
}
